import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import {
  defaultBranchSettings,
  type BranchSettings,
} from "@/mocks/branch/settings";

interface SettingsContextValue {
  settings: BranchSettings;
  saveAll: (next: BranchSettings) => void;
}

const STORAGE_KEY = "yc_branch_settings";

const SettingsContext = createContext<SettingsContextValue | undefined>(undefined);

function readStored(): BranchSettings {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return defaultBranchSettings;
    const parsed = JSON.parse(raw) as BranchSettings;
    return {
      ...defaultBranchSettings,
      ...parsed,
      pickupPolicy: {
        ...defaultBranchSettings.pickupPolicy,
        ...parsed.pickupPolicy,
        methods: {
          ...defaultBranchSettings.pickupPolicy.methods,
          ...parsed.pickupPolicy?.methods,
        },
      },
      delivery: {
        ...defaultBranchSettings.delivery,
        ...parsed.delivery,
      },
    };
  } catch {
    return defaultBranchSettings;
  }
}

export function SettingsProvider({ children }: { children: ReactNode }) {
  const [settings, setSettings] = useState<BranchSettings>(() => readStored());

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
    } catch {
      // ignore storage failures
    }
  }, [settings]);

  const saveAll = useCallback((next: BranchSettings) => {
    setSettings(next);
  }, []);

  const value = useMemo<SettingsContextValue>(
    () => ({ settings, saveAll }),
    [settings, saveAll],
  );

  return <SettingsContext.Provider value={value}>{children}</SettingsContext.Provider>;
}

export function useSettings(): SettingsContextValue {
  const ctx = useContext(SettingsContext);
  if (!ctx) {
    throw new Error("useSettings must be used within a SettingsProvider");
  }
  return ctx;
}